import { Stack } from "@mui-wrapper";
import { DateTimeInput } from "@custom-types/base-types";
import { DateTimeUtils } from "@stellar/utils";
import { Text, Dialog, Button } from "@flat-ui";
import { useState } from "react";

interface PlanRangeInfoProps {
  /** Date to render for the start of the plan */
  startDate: DateTimeInput;

  /** Date to render for the end of the plan */
  endDate: DateTimeInput;
}

/**
 * A panel about informing on how the selected dates affect customers in different time zones
 */
export function PlanRangeInfo({
  startDate,
  endDate,
}: PlanRangeInfoProps): React.JSX.Element {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const formattedStartDate = formatDateForTimezones(
    DateTimeUtils.getDayEdge({ date: startDate, edge: "start" })
  );

  const formattedEndDate = formatDateForTimezones(
    DateTimeUtils.getDayEdge({ date: endDate, edge: "end" })
  );

  return (
    <>
      <Button
        onClick={() => setIsDialogOpen(true)}
        variant="ghost"
        sx={{ px: 0 }}
      >
        When do plans start and end?
      </Button>

      <Dialog
        open={isDialogOpen}
        title="Plan Range Info"
        onClose={() => setIsDialogOpen(false)}
      >
        <Stack sx={{ gap: 2 }}>
          <Text variant="bodyL">
            Plans will start and end at midnight of the <i>UTC time zone</i>.
            Below is a list of <b>example</b> customer locations and when the
            plan with the selected dates will start and end for customers in
            those locations.
          </Text>

          <Text variant="bodyL">
            Please take particular note of the difference in calendar dates.
            Consider letting the plan start earlier or end later in order to
            provide an uninterrupted customer experience.
          </Text>

          <ul>
            <TimeZoneInfoListItem
              title="US west coast"
              startText={formattedStartDate.LA}
              endText={formattedEndDate.LA}
            />
            <TimeZoneInfoListItem
              title="Germany"
              startText={formattedStartDate.DE}
              endText={formattedEndDate.DE}
            />
            <TimeZoneInfoListItem
              title="Japan"
              startText={formattedStartDate.JP}
              endText={formattedEndDate.JP}
            />
          </ul>
        </Stack>
      </Dialog>
    </>
  );
}

interface TimeZoneInfoListItemProps {
  /** Title of the list item */
  title: string;

  /** String to show for the start entry */
  startText: string;

  /** String to show for the end entry */
  endText: string;
}

/**
 * List item that showing a bold title and another sub-list with two entries
 */
function TimeZoneInfoListItem({
  title,
  startText,
  endText,
}: TimeZoneInfoListItemProps): React.JSX.Element {
  return (
    <li>
      <b>{title}</b>
      <ul>
        <li>Start: {startText}</li>
        <li>End: {endText}</li>
      </ul>
    </li>
  );
}

interface FormattedDateForTimezones {
  /** Date as string in local time of Los Angeles */
  LA: string;

  /** Date as string in local time in Germany */
  DE: string;

  /** Date as string in local time of Japan */
  JP: string;
}

/**
 * Format the provided date into strings using the local times of Los Angeles, Germany and Japan
 */
function formatDateForTimezones(date: Date): FormattedDateForTimezones {
  const stringFormat = "en-US";

  const stringOptions: Intl.DateTimeFormatOptions = {
    // Show date in this format: "June 24, 21" (example)
    year: "2-digit",
    month: "long",
    day: "2-digit",

    // Show time of day in this format: "1:59 AM" (example)
    hour: "2-digit",
    minute: "2-digit",

    // Show time zone in long format: "Central European Summer Time" (example)
    // Note: The other option here is "short", which will often only show the GMT offset and not the time zone's name
    timeZoneName: "long",
  };

  const timezones: {
    short: keyof FormattedDateForTimezones;
    long: string;
  }[] = [
    { short: "LA", long: "America/Los_Angeles" },
    { short: "DE", long: "Europe/Berlin" },
    { short: "JP", long: "Japan" },
  ];

  return timezones.reduce((acc, timezone) => {
    acc[timezone.short] = date.toLocaleString(stringFormat, {
      ...stringOptions,

      timeZone: timezone.long,
    });

    return acc;
  }, {} as FormattedDateForTimezones);
}
